import React, { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { DashboardContent } from '@/layouts/dashboard';
import { FiDownload } from 'react-icons/fi';
import { TiaBanner } from '@/components/custom/tia-banner';
import { CURSOS } from '@/pages/dashboard/home-teacher/teacher-character';
import gradeIcon from '@/assets/images/icons/calendar.png';
import { EJASchedule, EM1Schedule, EM2Schedule, EM3Schedule } from '../schedule-catalog';

const GREETING_TEXT = "Aqui você encontra a grade horária dos seus cursos! Toque em 'Ver grade' pra ver e baixar.";

// Mesmos 5 tokens de accent de src/pages/dashboard/home-teacher/teacher-shortcuts.jsx
// (SHORTCUT_ACCENTS), ciclando por índice do card já que não há uma categoria
// própria por curso aqui.
const ACCENTS = [
  { bg: 'var(--color-bg-teal-soft)', button: 'var(--color-third)' }, // ciano
  { bg: 'var(--color-bg-lilac-soft)', button: 'var(--color-secondary)' }, // lilás
  { bg: 'color-mix(in srgb, var(--color-badge-gold) 30%, white)', button: 'var(--color-badge-gold-text)' }, // dourado
  { bg: 'var(--color-bg-mission-soft)', button: 'var(--color-mission)' }, // laranja
  { bg: '#FFE7E5', button: '#FF4C6F' } // rosa
];

function handleDownload(url) {
  const link = document.createElement('a');
  link.href = url;
  link.download = 'grade-horaria.jpg';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}

// Mesma técnica visual de ShortcutActionCard (teacher-shortcuts.jsx) — troca
// só o <Link to> por um <button onClick>, já que aqui a ação abre um modal
// em vez de navegar.
function ScheduleCourseCard({ accent, illustration, title, subtitle, ctaLabel, onClick }) {
  return (
    <div
      className="flex items-end gap-4 rounded-[16px] p-4"
      style={{ background: accent.bg }}
      data-testid="schedule-course-card"
      data-title={title}
    >
      <div className="flex flex-1 flex-col items-start gap-2">
        <img
          src={illustration}
          alt=""
          aria-hidden="true"
          className="h-12 w-12 object-contain drop-shadow-sm"
        />
        <div className="flex flex-col gap-0.5">
          <span
            className="block text-base font-extrabold"
            style={{ color: '#222731' }}
          >
            {title}
          </span>
          {subtitle && <span className="block text-xs font-medium text-neutral-500">{subtitle}</span>}
        </div>
      </div>

      <button
        type="button"
        onClick={onClick}
        className="flex h-9 shrink-0 items-center justify-center rounded-full px-4 text-sm font-bold text-white transition-transform duration-100 active:scale-[0.97]"
        style={{
          background: accent.button,
          boxShadow: `0 3px 0 color-mix(in srgb, ${accent.button} 72%, #000), 0 4px 8px rgba(0,0,0,0.18)`
        }}
      >
        {ctaLabel}
      </button>
    </div>
  );
}

export function CourseSchedulePlayful(props) {
  const connection = localStorage.getItem('DBNumber');

  const { uri } = props;
  const [openModal, setOpenModal] = useState(false);
  const [selectedSchedule, setSelectedSchedule] = useState(null);
  const [currentSchedule, setCurrentSchedule] = useState(null);

  useEffect(() => {
    switch (connection) {
      case '4': // EJA
        setCurrentSchedule(EJASchedule);
        break;
      case '3': // EM1
        setCurrentSchedule(EM1Schedule);
        break;
      case '2': // EM2
        setCurrentSchedule(EM2Schedule);
        break;
      case '1': // EM3
        setCurrentSchedule(EM3Schedule);
        break;
      default:
        setCurrentSchedule(null);
    }
  }, [connection]);

  const breadcrumbs = {
    title: 'Cursos e Aulas',
    nav: [
      {
        route: uri,
        name: 'Horários',
        isActive: true
      }
    ]
  };

  return (
    <DashboardContent breadcrumbs={breadcrumbs}>
      <div className="flex flex-col gap-4">
        <div className="relative sm:mt-16">
          <TiaBanner
            className="h-full"
            image={CURSOS}
            imageAlt="Personagem do professor"
            imageHeight={190}
            imageOffset={280}
            bubbleCorner="tl"
            bubbleMaxWidth={400}
            text={GREETING_TEXT}
          />
        </div>

        {currentSchedule && (
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {currentSchedule.map((item, index) => (
              <ScheduleCourseCard
                key={`${item.course}-${item.subtitle}`}
                accent={ACCENTS[index % ACCENTS.length]}
                illustration={gradeIcon}
                title={item.course}
                subtitle={item.subtitle}
                ctaLabel="Ver grade"
                onClick={() => {
                  setSelectedSchedule(item);
                  setOpenModal(true);
                }}
              />
            ))}
          </div>
        )}
      </div>

      <Dialog
        open={openModal}
        onOpenChange={() => setOpenModal(false)}
      >
        <DialogContent size="full">
          <DialogTitle className="sr-only">{selectedSchedule?.course}</DialogTitle>
          <FiDownload
            className="absolute right-12 top-3.5 h-5 w-5 cursor-pointer text-primary"
            onClick={() => handleDownload(selectedSchedule?.schedule)}
          />
          {selectedSchedule && (
            <div className="mt-3 flex justify-center">
              <img
                src={selectedSchedule.schedule}
                alt="Horário"
                // DialogContent já reserva max-h-[90vh] com p-6 (48px) e este
                // wrapper soma mt-3 (12px) -> ~60px de moldura ao redor. Sem
                // descontar isso, uma grade em retrato (altura-limitada) podia
                // estourar o teto e a modal voltava a rolar (achado da review
                // final) — daí o calc() em vez de um 85vh cru.
                className="max-h-[calc(85vh-60px)] w-auto max-w-full object-contain"
              />
            </div>
          )}
        </DialogContent>
      </Dialog>
    </DashboardContent>
  );
}
